import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import { Button } from 'components/ui/button'
import { useAddSettings, useSettingsData, useUpdateSettings } from 'hooks/settings'
import { useNotificationBar } from 'context/notificationContext'
import InputField from 'components/styledFormElements/InputField'
import FormSelect from 'components/forms/FormSelect'

interface PaymentGatewayFormData {
  merchantId: string
  clientId: string
  clientSecret: string
  paymentProvider: string
}

const PAYMENT_PROVIDER_OPTIONS = [
  { id: 'phonepe', name: 'PhonePe' },
  { id: 'razorpay', name: 'Razorpay' },
  { id: 'stripe', name: 'Stripe' },
  { id: 'paytm', name: 'Paytm' },
]

const PaymentGatewaySettings = () => {
  const notification = useNotificationBar()

  const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
  const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
  const { data: settingsData, isLoading , refetch} = useSettingsData()
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PaymentGatewayFormData>({
    defaultValues: {
      merchantId: '',
      clientId: '',
      clientSecret: '',
      paymentProvider: '',
    },
  })

  // Map API data to form values
  const mapDataToFormValues = (dataArray: any[]): PaymentGatewayFormData => {
    const formValues: PaymentGatewayFormData = {
      merchantId: '',
      clientId: '',
      clientSecret: '',
      paymentProvider: '',
    }

    const paymentGatewayItem = dataArray.find(
      (item) => item.slug === 'payment_gateway'
    )

    if (paymentGatewayItem?.settings) {
      const { settings } = paymentGatewayItem
      formValues.merchantId = settings.merchant_id || ''
      formValues.clientId = settings.client_id || ''
      formValues.clientSecret = settings.client_secret || ''
      formValues.paymentProvider = settings.payment_provider || ''
    }

    return formValues
  }

  // Compute default values from API data
  const formDefaultValues = useMemo(() => {
    if (!isLoading && settingsData?.data) {
      const dataArray = Array.isArray(settingsData.data) && settingsData.data.length > 0
        ? settingsData.data
        : []
      return mapDataToFormValues(dataArray)
    }
    return {
      merchantId: '',
      clientId: '',
      clientSecret: '',
      paymentProvider: '',
    }
  }, [settingsData?.data, isLoading])

  // Reset form when data loads
  useEffect(() => {
    if (!isLoading) {
      reset(formDefaultValues)
    }
  }, [formDefaultValues, isLoading, reset])

  const onSubmit = (data: PaymentGatewayFormData) => {
    const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0
    const existingPaymentGatewaySetting = hasExistingData
      ? settingsData.data.find((item: any) => item.slug === 'payment_gateway')
      : null

    const payload = {
      slug: 'payment_gateway',
      settings: {
        merchant_id: data.merchantId,
        client_id: data.clientId,
        client_secret: data.clientSecret,
        payment_provider: data.paymentProvider,
      },
      status: 1,
      is_delete: 0,
    }

    if (existingPaymentGatewaySetting?.id) {
      updateSettings(
        {
          data: payload,
          setting_id: existingPaymentGatewaySetting.id,
        },
        {
          onSuccess: () => {
            notification.push({
              title: 'Success',
              type: 'success',
              message: 'Payment gateway settings updated successfully',
            })
            refetch()
          },
          onError: (error: any) => {
            notification.push({
              title: 'Error',
              type: 'error',
              message: 'Failed to update payment gateway settings',
            })
          },
        }
      )
    } else {
      addSettings(
        { data: payload },
        {
          onSuccess: () => {
            notification.push({
              title: 'Success',
              type: 'success',
              message: 'Payment gateway settings created successfully',
            })
            refetch()
          },
          onError: (error: any) => {
            notification.push({
              title: 'Error',
              type: 'error',
              message: 'Failed to create payment gateway settings',
            })
          },
        }
      )
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md'>
        <aside className="flex flex-auto items-center gap-2">
          <CreditCardIcon className="h-6 w-6" />
          <h4 className="text-xl">Payment Gateway Settings</h4>
        </aside>

        <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Provider Field */}
            <div>
              <FormSelect
                label="Payment Provider"
                register={register('paymentProvider', {
                  required: 'Payment Provider is required',
                  validate: (value) =>
                    (value && value !== '0' && value !== '') ||
                    'Please select a payment provider',
                })}
                errors={errors as any}
                options={PAYMENT_PROVIDER_OPTIONS}
                defaultOptionLabel="Select Payment Provider"
              />
            </div>

            {/* Merchant Id Field */}
            <div>
              <InputField
                label="Merchant Id"
                type="text"
                required
                placeholder="Enter Merchant Id"
                register={register('merchantId', {
                  required: 'Merchant Id is required',
                })}
                error={errors?.merchantId?.message as string | ''}
              />
            </div>

            {/* Client Id Field */}
            <div>
              <InputField
                label="Client Id"
                type="text"
                required
                placeholder="Enter Client Id"
                register={register('clientId', {
                  required: 'Client Id is required',
                })}
                error={errors?.clientId?.message as string | ''}
              />
            </div>

            {/* Client Secret Field */}
            <div>
              <InputField
                label="Client Secret"
                type="text"
                required
                placeholder="Enter Client Secret"
                register={register('clientSecret', {
                  required: 'Client Secret is required',
                })}
                error={errors?.clientSecret?.message as string | ''}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-left my-6">
          <Button color="blue" type='submit' isLoading={updateLoading || addLoading}>
            Save Gateway Settings
          </Button>
        </div>
      </div>
    </form>
  )
}

export default PaymentGatewaySettings